import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { timeAgo, formatSignalSource } from "@/lib/utils-ros";
import { ActionEditor } from "@/components/revenueos/ActionEditor";
import { EditableField } from "@/components/revenueos/EditableField";
import { ChevronDown, ChevronRight, Loader2, Sparkles, Layers, Trash2, Globe, Plus, Filter, X, Radio } from "lucide-react";
import { PartnerAssignmentPanel } from "@/components/revenueos/PartnerAssignment";
import { MeetingLogInline } from "@/components/revenueos/MeetingLog";
import { CreateSignalModal } from "@/components/revenueos/CreateSignalModal";
import { PageToolbar } from "@/components/revenueos/PageToolbar";
import { CustomerCard } from "@/components/revenueos/CustomerCard";
import { toast } from "sonner";
import { canCrawlSignals, useAuth } from "@/lib/auth";

function StrengthBar({ v }) {
  const color = v >= 85 ? "#16A34A" : v >= 70 ? "#E28A23" : "#DC2626";
  return (
    <div className="flex items-center justify-center gap-2 w-28">
      <div className="flex-1 h-1.5 bg-black/[0.06] rounded-full overflow-hidden">
        <div className="h-full" style={{ width: `${v}%`, background: color }} />
      </div>
      <span className="text-[12px] tabular-nums text-[#526D7A] w-6 text-right">{v}</span>
    </div>
  );
}

export default function Signals() {
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, canWrite } = useAuth();
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [crawling, setCrawling] = useState(false);
  const [crawlStatus, setCrawlStatus] = useState(null);
  const pollRef = useRef(null);
  const [query, setQuery] = useState("");
  const [customerCid, setCustomerCid] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [strengthFilter, setStrengthFilter] = useState("all");
  const canCrawl = canCrawlSignals(user);
  const canEditSignals = canWrite("signals");
  const canCreate = canEditSignals;
  const canLogMeetings = canWrite("customers");

  async function load(opts = {}) {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/signals");
      setSignals(data);
    } catch {
      if (!silent) toast.error("Could not load signals");
      setSignals([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const refreshCrawlStatus = useCallback(async () => {
    if (!canCrawl) return null;
    try {
      const { data } = await api.get("/admin/crawl-signals/status");
      setCrawlStatus(data);
      return data;
    } catch {
      return null;
    }
  }, [canCrawl]);

  useEffect(() => {
    if (canCrawl) refreshCrawlStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [canCrawl, refreshCrawlStatus]);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) setQuery(q);
  }, [searchParams]);

  async function saveAction(sid, action) {
    const { data } = await api.patch(`/signals/${sid}`, { next_action: action, status: "analysed" });
    setSignals(s => s.map(x => x.id === sid ? { ...x, next_action: action } : x));
    return data;
  }

  async function regenerateAI(sid) {
    const { data } = await api.post(`/signals/${sid}/regenerate-ai`);
    if (data.ai_summary) setSignals(s => s.map(x => x.id === sid ? { ...x, ai_summary: data.ai_summary } : x));
    return data;
  }

  async function deleteSignal(sid, name) {
    if (!window.confirm(`Delete this signal for ${name || "this company"}?\n\nIt will be moved to Lost. You can restore it using the Lost button on the right side of the pipeline bar.`)) return;
    try {
      await api.delete(`/signals/${sid}?reason=user_deleted`);
      toast.success("Moved to Lost · restore from the pipeline bar");
      setSignals(s => s.filter(x => x.id !== sid));
    } catch (e) { toast.error("Delete failed"); }
  }

  async function runCrawl() {
    if (!canCrawl) {
      toast.error("Crawl requires Admin, Growth & Marketing, or Ergoniq Sales role");
      return;
    }
    setCrawling(true);
    try {
      const { data } = await api.post("/admin/crawl-signals", { force: true });
      toast.info(data.message || `Scanning ${data.companies_total} Universe companies…`);

      const poll = async () => {
        const status = await refreshCrawlStatus();
        if (!status?.running) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setCrawling(false);
          const last = status?.last_run;
          const created = last?.signals_created ?? 0;
          const scanned = last?.companies_scanned ?? data.companies_total;
          const mode = last?.ai_used ? "AI crawl" : "Crawl";
          if (last?.status === "failed") {
            toast.error(last.error || "Crawl failed");
          } else {
            toast.success(`${mode} complete — ${created} signal${created === 1 ? "" : "s"} from ${scanned} companies`);
          }
          await load({ silent: true });
        }
      };

      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(poll, 2000);
      setTimeout(poll, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Crawl failed");
      setCrawling(false);
    }
  }

  const crawlProgress = crawlStatus?.active_run;
  const lastCrawl = crawlStatus?.last_run;
  const schedule = crawlStatus?.schedule;

  const visibleSignals = signals.filter(s => {
    const q = query.trim().toLowerCase();
    if (q && ![s.company?.name, s.source, s.signal_type, s.intent, s.ai_summary].filter(Boolean).join(" ").toLowerCase().includes(q)) return false;
    if (sourceFilter !== "all" && String(s.source) !== sourceFilter) return false;
    if (typeFilter !== "all" && String(s.signal_type) !== typeFilter) return false;
    if (strengthFilter === "strong" && s.strength < 85) return false;
    if (strengthFilter === "mid" && (s.strength < 70 || s.strength >= 85)) return false;
    if (strengthFilter === "low" && s.strength >= 70) return false;
    return true;
  });
  const signalSources = [...new Set(signals.map(s => s.source).filter(Boolean))].sort();
  const signalTypes = [...new Set(signals.map(s => s.signal_type).filter(Boolean))].sort();
  const activeFilterCount = [sourceFilter !== "all", typeFilter !== "all", strengthFilter !== "all"].filter(Boolean).length;

  function clearFilters() {
    setSourceFilter("all");
    setTypeFilter("all");
    setStrengthFilter("all");
  }

  return (
    <div className="min-h-full bg-[#F7FAFC] pb-12" data-testid="signals-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-6 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#FFFFFF]">Signals</h1>
        <p className="text-[13px] text-white/75 mt-1">Material intelligence from Universe — leadership changes, investments, and buying intent.</p>
        {canCrawl && schedule?.enabled && (
          <p className="text-[11px] text-white/55 mt-2" data-testid="crawl-schedule-hint">
            Scheduled daily at {String(schedule.hour).padStart(2, "0")}:{String(schedule.minute).padStart(2, "0")} {schedule.timezone}
            {lastCrawl?.finished_at && !crawling ? ` · Last run ${timeAgo(lastCrawl.finished_at)} (${lastCrawl.signals_created ?? 0} signals)` : ""}
          </p>
        )}
      </section>

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-5">
        <PageToolbar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search signals by company, source, or intent…"
          entityLabel="signals"
          count={visibleSignals.length}
          variant="universe"
          afterPrompts={!authLoading ? (
            <button onClick={runCrawl} disabled={crawling || !canCrawl} data-testid="crawl-btn"
                    title={canCrawl ? "Force-crawl every Universe company for leadership changes, investments, and material events" : "Requires Admin, Growth & Marketing, or Ergoniq Sales role"}
                    className={`shrink-0 h-9 px-3 rounded-full text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-50 shadow-[0_5px_14px_rgba(226,138,35,0.18)] ${canCrawl ? "bg-[#E28A23] hover:bg-[#C97215] !text-white" : "border border-[#C9DCE6] bg-white text-[#78909C] cursor-not-allowed"}`}>
              {crawling ? <Loader2 size={13} className="animate-spin" /> : <Globe size={13} />}
              {crawling ? (crawlProgress ? `Crawling ${crawlProgress.companies_scanned ?? 0}/${crawlStatus?.companies_total ?? "?"}…` : "Crawling Universe…") : "Web Crawl"}
            </button>
          ) : null}
          right={(
            <div className="flex items-center justify-end gap-2 flex-wrap shrink-0">
              {!authLoading && canCreate && (
                <button onClick={() => setCreateOpen(true)} data-testid="create-signal-btn"
                        className="h-9 px-3 rounded-lg border border-[#BFD6E1] bg-white text-[#07539A] hover:border-[#00A2D4] text-[12px] font-medium flex items-center gap-1.5 shadow-[0_4px_12px_rgba(7,83,154,0.06)]">
                  <Plus size={13} /> New signal
                </button>
              )}
              <div className="relative">
                <button type="button" onClick={() => setFilterOpen(open => !open)} data-testid="signals-filter-btn"
                        className={`h-9 px-3 rounded-lg border text-[12px] font-medium flex items-center gap-2 shadow-[0_4px_12px_rgba(7,83,154,0.06)] transition-colors ${filterOpen || activeFilterCount ? "border-[#07539A] bg-[#E3F2F8] text-[#07539A]" : "border-[#BFD6E1] bg-white text-[#243B53] hover:border-[#07539A]"}`}>
                  <Filter size={14} /> Filter
                  {activeFilterCount > 0 && <span className="w-5 h-5 rounded-full bg-[#07539A] !text-white text-[10px] flex items-center justify-center">{activeFilterCount}</span>}
                </button>
                {filterOpen && (
                  <div className="absolute right-0 top-11 z-30 w-[min(88vw,30rem)] rounded-2xl border border-[#C9DCE6] bg-white shadow-[0_20px_50px_rgba(7,83,154,0.20)] p-4" data-testid="signals-filter-panel">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="font-display text-lg text-[#07539A]">Filter signals</div>
                        <div className="text-[10px] text-[#78909C] mt-0.5">Combine filters to narrow the intent stream.</div>
                      </div>
                      <button type="button" onClick={() => setFilterOpen(false)} aria-label="Close filters" className="w-8 h-8 rounded-full hover:bg-[#EDF7FB] text-[#607D8B] flex items-center justify-center"><X size={14} /></button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 [&_select]:w-full [&_select]:h-10 [&_select]:rounded-xl [&_select]:border [&_select]:border-[#B8D3DF] [&_select]:bg-[#F8FCFE] [&_select]:px-3 [&_select]:text-[12px] [&_select]:outline-none [&_select:focus]:border-[#00A2D4]">
                      <SignalFilterSelect label="Signal source" value={sourceFilter} onChange={setSourceFilter} options={[
                        { key: "all", label: "All sources" }, ...signalSources.map(value => ({ key: value, label: formatSignalSource(value) })),
                      ]} />
                      <SignalFilterSelect label="Signal type" value={typeFilter} onChange={setTypeFilter} options={[
                        { key: "all", label: "All types" }, ...signalTypes.map(value => ({ key: value, label: value.replaceAll("_", " ") })),
                      ]} />
                      <SignalFilterSelect label="Signal strength" value={strengthFilter} onChange={setStrengthFilter} options={[
                        { key: "all", label: "All strengths" }, { key: "strong", label: "Strong · 85+" },
                        { key: "mid", label: "Mid · 70–84" }, { key: "low", label: "Low · below 70" },
                      ]} />
                    </div>
                    <div className="mt-4 pt-3 border-t border-[#E1EBF0] flex items-center justify-between">
                      <button type="button" onClick={clearFilters} className="text-[11px] text-[#607D8B] hover:text-[#07539A]">Clear all</button>
                      <button type="button" onClick={() => setFilterOpen(false)} className="h-9 px-4 rounded-xl bg-[#07539A] !text-white text-[12px] font-medium">Show {visibleSignals.length} signals</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        />

      <div className="border border-[#C9DCE6] rounded-[22px] overflow-x-auto bg-white shadow-[0_10px_28px_rgba(7,83,154,0.08)]" data-testid="signals-table-scroll">
        <div className="grid min-w-[1040px] grid-cols-[minmax(180px,1fr)_minmax(260px,2fr)_minmax(110px,0.8fr)_minmax(110px,0.8fr)_140px_18px] gap-3 items-center px-4 py-3 bg-[#F3FAFD] border-b border-[#D7E8F0] text-[11px] font-semibold text-[#07539A]">
          <div>Company</div>
          <div>Signal</div>
          <div className="text-center">Signal source</div>
          <div className="text-center">Signal type</div>
          <div className="text-center">Signal strength</div>
          <div />
        </div>
        {loading ? (
          <div className="p-8 text-center text-[var(--text-muted)]">Loading signals…</div>
        ) : (
          <div className="min-w-[1040px] divide-y divide-[#E1EBF0]">
            {visibleSignals.map(s => {
              const isExp = expanded === s.id;
              return (
                <div key={s.id} data-testid={`signal-row-${s.id}`}>
                  <button
                    onClick={() => setExpanded(isExp ? null : s.id)}
                    data-testid={`signal-expand-${s.id}`}
                    className="w-full text-left grid grid-cols-[minmax(180px,1fr)_minmax(260px,2fr)_minmax(110px,0.8fr)_minmax(110px,0.8fr)_140px_18px] gap-3 items-center px-4 py-4 hover:bg-[#F8FCFE] transition-colors"
                  >
                    <div className="min-w-0 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-sm bg-black/[0.05] border border-black/10 flex items-center justify-center text-[12px] text-[#526D7A] shrink-0">
                        {(s.company?.name || "?").substring(0, 2).toUpperCase()}
                      </div>
                      <span className="min-w-0 text-[12px] font-medium text-[#455A64] truncate">{s.company?.name || "—"}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] leading-snug text-[#263F4D]">{s.intent}</div>
                      <div className="mt-1.5 text-[11px] text-[#607D8B]">{timeAgo(s.created_at)}</div>
                    </div>
                    <div className="text-center text-[12px] text-[#526D7A]">{formatSignalSource(s.source)}</div>
                    <div className="text-center text-[12px] text-[#526D7A] capitalize">{s.signal_type.replaceAll("_", " ")}</div>
                    <div className="flex items-center justify-center shrink-0">
                      <StrengthBar v={s.strength} />
                    </div>
                    <div className="flex items-center justify-center text-[var(--text-dim)]">
                      {isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </div>
                  </button>
                  {isExp && (
                    <div className="sticky left-0 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] md:static md:w-auto md:max-w-none md:mx-2 mb-3 px-3 md:px-4 py-4 bg-[#EAF7FC] border border-[#BBD8E7] rounded-[22px] space-y-4 shadow-[0_10px_24px_rgba(7,83,154,0.07)]" data-testid={`signal-actions-${s.id}`}>
                      <button
                        onClick={() => setCustomerCid(s.company_id)}
                        data-testid={`customer-360-${s.id}`}
                        className="w-full min-h-11 px-4 rounded-[16px] bg-[#07539A] hover:bg-[#063F73] !text-white text-[12px] font-medium flex items-center justify-center gap-2 shadow-[0_7px_18px_rgba(7,83,154,0.16)]"
                      >
                        <Layers size={12} /> Open Customer 360 — {s.company?.name} · All history across stages
                      </button>
                      <div className="rounded-[20px] overflow-hidden bg-white border border-[#C9DCE6] shadow-[0_7px_20px_rgba(7,83,154,0.06)]">
                        <EditableField
                          label={<span className="flex items-center gap-2"><Radio size={13} /> Signal intent</span>}
                          value={s.intent}
                          testId={`signal-intent-${s.id}`}
                          multiline
                          readOnly={!canEditSignals}
                          className="border-0 rounded-none bg-transparent p-3 md:p-4"
                          labelClassName="text-[11px] uppercase tracking-widest text-[#07539A]"
                          inputClassName="leading-relaxed"
                          onSave={async (v) => {
                            const { data } = await api.patch(`/signals/${s.id}`, { intent: v.trim() });
                            setSignals((prev) => prev.map((x) => x.id === s.id ? { ...x, intent: v.trim() } : x));
                            const sync = data?.downstream_sync;
                            if (sync?.leads) toast.success(`Intent updated — synced to ${sync.leads} lead`);
                            else toast.success("Intent updated");
                          }}
                        />
                      </div>
                      <ActionEditor
                        actions={s.next_action || ""}
                        aiInsights={s.ai_summary}
                        single
                        title="Your next action"
                        accent="navy"
                        rounded
                        readOnly={!canEditSignals}
                        onSave={(v) => saveAction(s.id, v)}
                        onRegenerate={() => regenerateAI(s.id)}
                      />
                      <div className="rounded-[20px] overflow-hidden bg-white border border-[#C9DCE6] shadow-[0_7px_20px_rgba(7,83,154,0.05)] [&>[data-testid='partner-assignment']]:border-0">
                        <PartnerAssignmentPanel entityType="signal" entityId={s.id} accent="navy" />
                      </div>
                      <div className="rounded-[20px] overflow-hidden bg-white border border-[#C9DCE6] shadow-[0_7px_20px_rgba(7,83,154,0.05)] [&>[data-testid='meeting-log']]:border-0">
                        <MeetingLogInline entityType="signal" entityId={s.id} accent="navy" readOnly={!canLogMeetings} />
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-1">
                        {s.promoted_lead_id ? (
                          <Link
                            to="/leads"
                            data-testid={`view-lead-${s.id}`}
                            className="h-9 px-4 rounded-xl bg-[var(--success)]/10 border border-[var(--success)]/40 text-[var(--success)] text-[12px] font-medium flex items-center justify-center gap-2 hover:bg-[var(--success)]/15"
                          >
                            ✓ Lead already sent · View Lead →
                          </Link>
                        ) : canEditSignals && (
                          <button
                            onClick={async () => {
                              try {
                                await api.post(`/signals/${s.id}/promote-to-lead`, {
                                  urgency: "medium",
                                  pain_point: s.intent,
                                });
                                toast.success("Sent to Leads");
                                load({ silent: true });
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Could not promote signal");
                              }
                            }}
                            data-testid={`promote-lead-${s.id}`}
                            className="h-9 px-4 rounded-xl bg-[#E28A23] hover:bg-[#C97215] !text-white text-[12px] font-medium flex items-center justify-center gap-2 shadow-[0_8px_20px_rgba(226,138,35,0.26)]"
                          >
                            <Sparkles size={12} /> Send to Leads
                          </button>
                        )}
                        {canEditSignals && (
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteSignal(s.id, s.company?.name); }}
                          data-testid={`delete-signal-${s.id}`}
                          className="text-[11px] px-3 h-8 rounded-xl bg-white border border-[var(--danger)]/30 text-[var(--danger)] hover:bg-[var(--danger)]/5 flex items-center gap-1"
                        >
                          <Trash2 size={11} /> Delete signal
                        </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
      {customerCid && <CustomerCard companyId={customerCid} onClose={() => setCustomerCid(null)} />}
      <CreateSignalModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(sig) => {
          load({ silent: true });
          if (sig?.id) setExpanded(sig.id);
        }}
      />
    </div>
  );
}

function SignalFilterSelect({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-[#455A64] mb-1.5">{label}</span>
      <select value={value} onChange={event => onChange(event.target.value)}>
        {options.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
      </select>
    </label>
  );
}
